import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, CheckCircle2, History, Plus, RefreshCw, Send, Trash, X, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { api, type M365App, type M365Mailbox } from '@/lib/api';
import { NlDateInput } from '@/components/ui/nl-date-input';
import {
	backfillM365Mailbox,
	createM365App,
	createM365Mailbox,
	deleteM365App,
	deleteM365Mailbox,
	patchM365Mailbox,
	testM365Mailbox,
} from '@/lib/mutations';
import { formatDateTimeISO } from '@/lib/utils';

export const Route = createFileRoute('/admin/m365')({
	component: M365Page,
});

function M365Page() {
	const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
	const appsQuery = useQuery({ queryKey: ['m365-apps'], queryFn: () => api.m365Apps() });
	const mailboxesQuery = useQuery({ queryKey: ['m365-mailboxes'], queryFn: () => api.m365Mailboxes() });
	const entitiesQuery = useQuery({ queryKey: ['entities'], queryFn: () => api.entities() });

	const [showAppForm, setShowAppForm] = useState(false);
	const [showMailboxForm, setShowMailboxForm] = useState(false);

	if (meQuery.isLoading) return <div className="p-8 text-sm text-muted-foreground">Laden…</div>;
	if (meQuery.data?.role !== 'admin') {
		return (
			<div className="mx-auto max-w-md p-8 text-center text-sm">
				<AlertTriangle className="mx-auto mb-2 h-6 w-6 text-destructive" />
				Alleen admins kunnen Microsoft 365 mailbox-pull beheren.
			</div>
		);
	}

	const apps = appsQuery.data?.items ?? [];
	const mailboxes = mailboxesQuery.data?.items ?? [];

	return (
		<div className="mx-auto max-w-5xl space-y-6">
			<div className="flex items-center gap-3">
				<Button variant="ghost" size="sm" asChild>
					<Link to="/admin/entities">
						<ArrowLeft className="mr-1.5 h-4 w-4" />
						Terug
					</Link>
				</Button>
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Microsoft 365 mailbox-pull</h1>
					<p className="text-sm text-muted-foreground">
						Pull-flow vanuit gedeelde M365-mailboxen. Per Azure AD app + 1..N mailboxen. Cron polt elke
						minuut, screening via Mistral, alleen inkoopfacturen worden verwerkt + verplaatst.
					</p>
				</div>
			</div>

			<Card>
				<CardHeader className="flex flex-row items-center justify-between pb-3">
					<CardTitle className="text-sm font-medium">Azure AD app-registraties</CardTitle>
					<Button size="sm" onClick={() => setShowAppForm(true)}>
						<Plus className="mr-1.5 h-4 w-4" />
						App toevoegen
					</Button>
				</CardHeader>
				<CardContent className="space-y-2 text-sm">
					{apps.length === 0 && (
						<p className="text-muted-foreground">
							Nog geen apps. Maak eerst een Azure AD app-registratie aan met Mail.ReadWrite (application) +
							Application Access Policy (zie ADR 0006).
						</p>
					)}
					{apps.map((app) => (
						<AppRow key={app.id} app={app} />
					))}
				</CardContent>
			</Card>

			<Card>
				<CardHeader className="flex flex-row items-center justify-between pb-3">
					<CardTitle className="text-sm font-medium">Mailboxen</CardTitle>
					<Button size="sm" onClick={() => setShowMailboxForm(true)} disabled={apps.length === 0}>
						<Plus className="mr-1.5 h-4 w-4" />
						Mailbox toevoegen
					</Button>
				</CardHeader>
				<CardContent className="space-y-2 text-sm">
					{mailboxes.length === 0 && (
						<p className="text-muted-foreground">
							Nog geen mailboxen ingesteld. Voeg eerst een Azure AD app toe (hierboven), dan kun je een
							mailbox koppelen.
						</p>
					)}
					{mailboxes.map((mb) => (
						<MailboxRow key={mb.id} mailbox={mb} />
					))}
				</CardContent>
			</Card>

			{showAppForm && <AddAppDialog onClose={() => setShowAppForm(false)} />}
			{showMailboxForm && (
				<AddMailboxDialog
					apps={apps}
					entities={(entitiesQuery.data?.entities ?? []).map((e) => ({ code: e.code, name: e.name }))}
					onClose={() => setShowMailboxForm(false)}
				/>
			)}
		</div>
	);
}

function AppRow({ app }: { app: M365App }) {
	const queryClient = useQueryClient();
	const deleteMutation = useMutation({
		mutationFn: () => deleteM365App(app.id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['m365-apps'] }),
	});
	return (
		<div className="flex items-center justify-between gap-3 rounded-md border p-3">
			<div className="flex-1 min-w-0">
				<div className="font-medium">{app.display_name}</div>
				<div className="font-mono text-[11px] text-muted-foreground">
					tenant {app.tenant_id} · client {app.client_id}
				</div>
				{app.client_secret_expires_at && (
					<div className="text-[11px] text-muted-foreground">
						Secret verloopt: {app.client_secret_expires_at}
					</div>
				)}
			</div>
			<Badge variant={app.has_secret ? 'muted' : 'destructive'}>
				{app.has_secret ? 'secret OK' : 'geen secret'}
			</Badge>
			<Button
				size="sm"
				variant="ghost"
				className="text-destructive hover:bg-destructive/10"
				onClick={() => {
					if (window.confirm(`App "${app.display_name}" + alle gekoppelde mailboxen verwijderen?`)) {
						deleteMutation.mutate();
					}
				}}
			>
				<Trash className="h-4 w-4" />
			</Button>
		</div>
	);
}

function MailboxRow({ mailbox }: { mailbox: M365Mailbox }) {
	const queryClient = useQueryClient();
	const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
	const [showBackfill, setShowBackfill] = useState(false);
	const [showOutgoing, setShowOutgoing] = useState(false);

	const toggleMutation = useMutation({
		mutationFn: () => patchM365Mailbox(mailbox.id, { active: !mailbox.active }),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['m365-mailboxes'] }),
	});
	const testMutation = useMutation({
		mutationFn: () => testM365Mailbox(mailbox.id),
		onSuccess: (res) => {
			setTestResult({
				ok: res.ok,
				msg: res.ok
					? `OK — folder "${res.folder_display_name}" bevat ${res.folder_item_count} items.`
					: res.detail ?? 'onbekende fout',
			});
			setTimeout(() => setTestResult(null), 8000);
		},
		onError: (err) => {
			const msg = err instanceof Error ? err.message : String(err);
			setTestResult({ ok: false, msg });
		},
	});
	const deleteMutation = useMutation({
		mutationFn: () => deleteM365Mailbox(mailbox.id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['m365-mailboxes'] }),
	});

	return (
		<div className="space-y-2 rounded-md border p-3">
			<div className="flex items-center justify-between gap-3">
				<div className="flex-1 min-w-0">
					<div className="font-medium">{mailbox.mailbox_address}</div>
					<div className="text-[11px] text-muted-foreground">
						{mailbox.app_display_name} → {mailbox.entity_name} ({mailbox.entity_code}) ·{' '}
						{mailbox.processing_mode} · elke {mailbox.poll_interval_minutes} min
					</div>
				</div>
				<Badge variant={mailbox.active ? 'muted' : 'destructive'}>
					{mailbox.active ? 'actief' : 'gepauzeerd'}
				</Badge>
				<Button size="sm" variant="outline" onClick={() => testMutation.mutate()} disabled={testMutation.isPending}>
					<RefreshCw className={testMutation.isPending ? 'mr-1.5 h-3.5 w-3.5 animate-spin' : 'mr-1.5 h-3.5 w-3.5'} />
					Test
				</Button>
				<Button size="sm" variant="outline" onClick={() => setShowBackfill((v) => !v)}>
					<History className="mr-1.5 h-3.5 w-3.5" />
					Backfill
				</Button>
				<Button size="sm" variant="outline" onClick={() => setShowOutgoing((v) => !v)}>
					<Send className="mr-1.5 h-3.5 w-3.5" />
					Verkoop-config
				</Button>
				<Button size="sm" variant="ghost" onClick={() => toggleMutation.mutate()}>
					{mailbox.active ? 'Pauzeer' : 'Activeer'}
				</Button>
				<Button
					size="sm"
					variant="ghost"
					className="text-destructive hover:bg-destructive/10"
					onClick={() => {
						if (window.confirm(`Mailbox-koppeling voor ${mailbox.mailbox_address} verwijderen?`)) {
							deleteMutation.mutate();
						}
					}}
				>
					<Trash className="h-4 w-4" />
				</Button>
			</div>
			<div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
				<span>
					Laatste pol: {mailbox.last_polled_at ? formatDateTimeISO(mailbox.last_polled_at) : 'nooit'}
				</span>
				{mailbox.last_error && <span className="text-destructive">Fout: {mailbox.last_error}</span>}
			</div>
			{testResult && (
				<div
					className={
						testResult.ok
							? 'flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900'
							: 'flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive'
					}
				>
					{testResult.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
					{testResult.msg}
				</div>
			)}
			{showBackfill && <BackfillPanel mailboxId={mailbox.id} onClose={() => setShowBackfill(false)} />}
			{showOutgoing && <OutgoingConfigPanel mailbox={mailbox} onClose={() => setShowOutgoing(false)} />}
		</div>
	);
}

function parsePatterns(raw: string | null): string[] {
	if (!raw) return [];
	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter((p): p is string => typeof p === 'string') : [];
	} catch {
		return [];
	}
}

function OutgoingConfigPanel({ mailbox, onClose }: { mailbox: M365Mailbox; onClose: () => void }) {
	const queryClient = useQueryClient();
	const [folderName, setFolderName] = useState(mailbox.outgoing_folder_name ?? '');
	const [senderText, setSenderText] = useState(parsePatterns(mailbox.outgoing_sender_patterns).join('\n'));
	const [subjectText, setSubjectText] = useState(parsePatterns(mailbox.outgoing_subject_patterns).join('\n'));
	const [matchMode, setMatchMode] = useState<'or' | 'and'>(mailbox.outgoing_match_mode);
	const [error, setError] = useState<string | null>(null);

	const splitLines = (s: string) =>
		s
			.split('\n')
			.map((l) => l.trim())
			.filter((l) => l.length > 0);

	const mutation = useMutation({
		mutationFn: () => {
			const trimmedFolder = folderName.trim();
			const senderPatterns = splitLines(senderText);
			const subjectPatterns = splitLines(subjectText);
			const currentFolder = mailbox.outgoing_folder_name ?? '';
			const patch: Parameters<typeof patchM365Mailbox>[1] = {};
			if (trimmedFolder !== currentFolder) {
				// Empty input → null = clear; nieuwe naam → resolve op server.
				patch.outgoing_folder_name = trimmedFolder === '' ? null : trimmedFolder;
			}
			const currentSender = parsePatterns(mailbox.outgoing_sender_patterns);
			if (JSON.stringify(senderPatterns) !== JSON.stringify(currentSender)) {
				patch.outgoing_sender_patterns = senderPatterns;
			}
			const currentSubject = parsePatterns(mailbox.outgoing_subject_patterns);
			if (JSON.stringify(subjectPatterns) !== JSON.stringify(currentSubject)) {
				patch.outgoing_subject_patterns = subjectPatterns;
			}
			if (matchMode !== mailbox.outgoing_match_mode) {
				patch.outgoing_match_mode = matchMode;
			}
			if (Object.keys(patch).length === 0) throw new Error('Geen wijzigingen.');
			return patchM365Mailbox(mailbox.id, patch);
		},
		onSuccess: () => {
			setError(null);
			queryClient.invalidateQueries({ queryKey: ['m365-mailboxes'] });
			onClose();
		},
		onError: (err) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	return (
		<div className="space-y-3 rounded-md border border-amber-200 bg-amber-50/40 p-3 text-xs">
			<div className="flex items-center justify-between">
				<span className="font-medium text-amber-900">Verkoopfacturen-detectie</span>
				<Button size="sm" variant="ghost" onClick={onClose}>
					<X className="h-3.5 w-3.5" />
				</Button>
			</div>
			<p className="text-muted-foreground">
				Mails die als &quot;eigen verkoopfactuur / uitgaande kopie&quot; herkend worden, worden niet
				ingelezen. De afzender <span className="font-mono">{mailbox.mailbox_address}</span> telt impliciet
				al als eigen — onderstaande patterns zijn aanvullingen voor andere domeinen of typische subjects.
			</p>
			<div>
				<Label className="text-xs">Verplaats herkende verkoopfacturen naar map (optioneel)</Label>
				<Input
					value={folderName}
					onChange={(e) => setFolderName(e.target.value)}
					placeholder="bv. _Kopie verstuurde facturen — leeg = blijft in Inbox"
					disabled={mutation.isPending}
				/>
				<p className="mt-1 text-[11px] text-muted-foreground">
					Folder onder Inbox of top-level (4 niveaus diep doorzocht). Wordt bij opslaan resolved via Graph;
					bestaat de map niet, dan toont de save een fout.
				</p>
			</div>
			<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
				<div>
					<Label className="text-xs">Sender-patterns (één per regel)</Label>
					<textarea
						value={senderText}
						onChange={(e) => setSenderText(e.target.value)}
						className="mt-1 h-24 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
						placeholder={'@halderberge1.nl\n@halderberge2.nl'}
						disabled={mutation.isPending}
					/>
					<p className="mt-1 text-[11px] text-muted-foreground">
						Volledig email of <span className="font-mono">@domein.nl</span> (suffix). Hoofdletters
						maken niet uit.
					</p>
				</div>
				<div>
					<Label className="text-xs">Subject-patterns (één per regel)</Label>
					<textarea
						value={subjectText}
						onChange={(e) => setSubjectText(e.target.value)}
						className="mt-1 h-24 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
						placeholder={'Hierbij sturen wij u factuur\nVerkoopfactuur'}
						disabled={mutation.isPending}
					/>
					<p className="mt-1 text-[11px] text-muted-foreground">
						Case-insensitive substring-match in het onderwerp.
					</p>
				</div>
			</div>
			<div className="flex flex-wrap items-center gap-4">
				<span className="text-muted-foreground">Match-modus:</span>
				<label className="flex items-center gap-1.5">
					<input
						type="radio"
						name={`match-${mailbox.id}`}
						value="or"
						checked={matchMode === 'or'}
						onChange={() => setMatchMode('or')}
						disabled={mutation.isPending}
					/>
					<span>Sender <em>of</em> subject volstaat</span>
				</label>
				<label className="flex items-center gap-1.5">
					<input
						type="radio"
						name={`match-${mailbox.id}`}
						value="and"
						checked={matchMode === 'and'}
						onChange={() => setMatchMode('and')}
						disabled={mutation.isPending}
					/>
					<span>Sender <em>en</em> subject moeten beide matchen</span>
				</label>
			</div>
			{error && (
				<div className="flex items-center gap-2 rounded border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-destructive">
					<XCircle className="h-3.5 w-3.5" />
					{error}
				</div>
			)}
			<div className="flex justify-end gap-2">
				<Button size="sm" variant="outline" onClick={onClose}>
					Annuleren
				</Button>
				<Button size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
					{mutation.isPending ? 'Opslaan…' : 'Opslaan'}
				</Button>
			</div>
		</div>
	);
}

// Datetime-local geeft `YYYY-MM-DDTHH:mm` terug zonder timezone — die plakken
// we naïef als lokale tijd en converteren naar UTC ISO voor de API.
function localDatetimeToUtcIso(local: string): string {
	if (!local) return '';
	const d = new Date(local);
	if (isNaN(d.getTime())) return '';
	return d.toISOString();
}

interface BatchResult {
	processed: number;
	more_available: boolean;
	next_link: string | null;
	last_received_at: string | null;
}

function BackfillPanel({ mailboxId, onClose }: { mailboxId: string; onClose: () => void }) {
	const [since, setSince] = useState('');
	const [maxPerBatch, setMaxPerBatch] = useState(15);
	const [history, setHistory] = useState<BatchResult[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [nextLink, setNextLink] = useState<string | null>(null);

	const totalProcessed = history.reduce((s, b) => s + b.processed, 0);
	const lastBatch = history[history.length - 1];

	const mutation = useMutation({
		mutationFn: () => {
			const sinceIso = localDatetimeToUtcIso(since);
			if (!sinceIso) throw new Error('Vul een geldige startdatum in.');
			const body: { since: string; max?: number; next_link?: string } = { since: sinceIso, max: maxPerBatch };
			if (nextLink) body.next_link = nextLink;
			return backfillM365Mailbox(mailboxId, body);
		},
		onSuccess: (res) => {
			setError(null);
			setHistory((h) => [...h, res]);
			setNextLink(res.next_link);
		},
		onError: (err) => {
			const body =
				err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	return (
		<div className="space-y-3 rounded-md border border-blue-200 bg-blue-50/40 p-3 text-xs">
			<div className="flex items-center justify-between">
				<span className="font-medium text-blue-900">Backfill historische mails</span>
				<Button size="sm" variant="ghost" onClick={onClose}>
					<X className="h-3.5 w-3.5" />
				</Button>
			</div>
			<p className="text-muted-foreground">
				Haalt mails uit de Inbox vanaf een bepaalde tijdstempel via Microsoft Graph en jaagt ze door dezelfde
				screen+intake+move pipeline als de cron. Idempotent — al verwerkte messages worden geskipped.
				Doe het in batches als er veel mails zijn.
			</p>
			<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
				<div>
					<Label className="text-xs">Vanaf (lokale tijd)</Label>
					<Input
						type="datetime-local"
						value={since}
						onChange={(e) => {
							setSince(e.target.value);
							// Reset paginatie als de startdatum verandert.
							setNextLink(null);
							setHistory([]);
						}}
						disabled={mutation.isPending || nextLink !== null}
					/>
					{nextLink && (
						<p className="mt-1 text-[11px] text-muted-foreground">
							Vergrendeld zolang er meer batches zijn — annuleer om opnieuw te starten.
						</p>
					)}
				</div>
				<div>
					<Label className="text-xs">Max per batch</Label>
					<Input
						type="number"
						min={1}
						max={50}
						value={maxPerBatch}
						onChange={(e) => setMaxPerBatch(Number(e.target.value) || 15)}
						disabled={mutation.isPending}
					/>
				</div>
			</div>
			{error && (
				<div className="flex items-center gap-2 rounded border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-destructive">
					<XCircle className="h-3.5 w-3.5" />
					{error}
				</div>
			)}
			{history.length > 0 && (
				<div className="space-y-1 rounded border bg-background px-2 py-1.5 text-muted-foreground">
					<div>
						Totaal verwerkt: <span className="font-medium text-foreground">{totalProcessed}</span> in {history.length} batch{history.length === 1 ? '' : 'es'}.
					</div>
					{lastBatch?.last_received_at && (
						<div>
							Laatste mail: <span className="font-mono">{lastBatch.last_received_at}</span>
						</div>
					)}
					{lastBatch?.more_available ? (
						<div className="text-amber-700">Er zijn nog meer mails — klik &quot;Volgende batch&quot;.</div>
					) : (
						<div className="text-emerald-700">Klaar — geen mails meer ná deze tijdstempel.</div>
					)}
				</div>
			)}
			<div className="flex justify-end gap-2">
				{nextLink && (
					<Button
						size="sm"
						variant="outline"
						onClick={() => {
							setNextLink(null);
							setHistory([]);
							setError(null);
						}}
					>
						Annuleren
					</Button>
				)}
				<Button
					size="sm"
					onClick={() => mutation.mutate()}
					disabled={mutation.isPending || !since || (history.length > 0 && !nextLink)}
				>
					{mutation.isPending ? 'Bezig…' : nextLink ? 'Volgende batch' : 'Start'}
				</Button>
			</div>
		</div>
	);
}

function AddAppDialog({ onClose }: { onClose: () => void }) {
	const queryClient = useQueryClient();
	const [tenantId, setTenantId] = useState('');
	const [clientId, setClientId] = useState('');
	const [clientSecret, setClientSecret] = useState('');
	const [displayName, setDisplayName] = useState('');
	const [secretExpiry, setSecretExpiry] = useState('');
	const [error, setError] = useState<string | null>(null);

	const mutation = useMutation({
		mutationFn: () =>
			createM365App({
				tenant_id: tenantId.trim(),
				client_id: clientId.trim(),
				client_secret: clientSecret,
				display_name: displayName.trim(),
				client_secret_expires_at: secretExpiry || null,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['m365-apps'] });
			onClose();
		},
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	return (
		<div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
			<Card className="w-full max-w-xl">
				<CardHeader className="flex flex-row items-center justify-between pb-3">
					<CardTitle className="text-sm font-medium">Azure AD app toevoegen</CardTitle>
					<Button variant="ghost" size="sm" onClick={onClose}>
						<X className="h-4 w-4" />
					</Button>
				</CardHeader>
				<CardContent className="space-y-3 text-sm">
					<p className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
						Voor een nieuwe registratie: maak in Azure AD een app aan met Mail.ReadWrite (application) +
						admin consent + Application Access Policy. Daarna client_id/secret hier invullen.
					</p>
					<div>
						<Label>Display-naam</Label>
						<Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="bv. SureGroup mailbox reader" />
					</div>
					<div>
						<Label>Tenant ID</Label>
						<Input value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="GUID" className="font-mono" />
					</div>
					<div>
						<Label>Client ID</Label>
						<Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="GUID" className="font-mono" />
					</div>
					<div>
						<Label>Client Secret</Label>
						<Input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} className="font-mono" />
					</div>
					<div>
						<Label>Secret-vervaldatum (optioneel)</Label>
						<NlDateInput value={secretExpiry} onChange={(iso) => setSecretExpiry(iso)} />
					</div>
					{error && <p className="text-xs text-destructive">{error}</p>}
					<div className="flex justify-end gap-2 pt-2">
						<Button variant="outline" size="sm" onClick={onClose}>Annuleren</Button>
						<Button size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
							{mutation.isPending ? 'Opslaan…' : 'Opslaan'}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

function AddMailboxDialog({
	apps,
	entities,
	onClose,
}: {
	apps: M365App[];
	entities: Array<{ code: string; name: string }>;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();
	const [appId, setAppId] = useState(apps[0]?.id ?? '');
	const [mailboxAddress, setMailboxAddress] = useState('');
	const [entityCode, setEntityCode] = useState('');
	const [folderName, setFolderName] = useState('Verwerkt');
	const [processingMode, setProcessingMode] = useState<'sync' | 'batch'>('sync');
	const [pollInterval, setPollInterval] = useState(15);
	const [error, setError] = useState<string | null>(null);

	const mutation = useMutation({
		mutationFn: () =>
			createM365Mailbox({
				m365_app_id: appId,
				mailbox_address: mailboxAddress.trim(),
				default_entity_code: entityCode,
				processed_folder_name: folderName.trim(),
				processing_mode: processingMode,
				poll_interval_minutes: pollInterval,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['m365-mailboxes'] });
			onClose();
		},
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	return (
		<div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
			<Card className="w-full max-w-xl">
				<CardHeader className="flex flex-row items-center justify-between pb-3">
					<CardTitle className="text-sm font-medium">Mailbox toevoegen</CardTitle>
					<Button variant="ghost" size="sm" onClick={onClose}>
						<X className="h-4 w-4" />
					</Button>
				</CardHeader>
				<CardContent className="space-y-3 text-sm">
					<div>
						<Label>Azure AD app</Label>
						<Select value={appId} onChange={(e) => setAppId(e.target.value)}>
							{apps.map((a) => (
								<option key={a.id} value={a.id}>{a.display_name}</option>
							))}
						</Select>
					</div>
					<div>
						<Label>Mailbox-adres</Label>
						<Input value={mailboxAddress} onChange={(e) => setMailboxAddress(e.target.value)} placeholder="inkoop@suregroup.nl" />
					</div>
					<div>
						<Label>Default entity (voor inkoopfacturen)</Label>
						<Select value={entityCode} onChange={(e) => setEntityCode(e.target.value)}>
							<option value="">— kies entity —</option>
							{entities.map((e) => (
								<option key={e.code} value={e.code}>{e.name} ({e.code})</option>
							))}
						</Select>
					</div>
					<div>
						<Label>Submap-naam voor verwerkte mails</Label>
						<Input value={folderName} onChange={(e) => setFolderName(e.target.value)} placeholder="Verwerkt" />
						<p className="mt-1 text-[11px] text-muted-foreground">
							De submap moet bestaan onder Inbox of als top-level folder. Wij resolveen 'm via Graph en
							slaan het folder-id op.
						</p>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<Label>Processing-mode</Label>
							<Select value={processingMode} onChange={(e) => setProcessingMode(e.target.value as 'sync' | 'batch')}>
								<option value="sync">Sync (snel, normale prijs)</option>
								<option value="batch">Batch (50% korting, max 24u)</option>
							</Select>
						</div>
						<div>
							<Label>Poll-interval (minuten)</Label>
							<Input
								type="number"
								min={1}
								max={240}
								value={pollInterval}
								onChange={(e) => setPollInterval(Number(e.target.value) || 15)}
							/>
						</div>
					</div>
					{error && <p className="text-xs text-destructive">{error}</p>}
					<div className="flex justify-end gap-2 pt-2">
						<Button variant="outline" size="sm" onClick={onClose}>Annuleren</Button>
						<Button size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending || !appId || !mailboxAddress || !entityCode}>
							{mutation.isPending ? 'Toevoegen…' : 'Toevoegen'}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
